import type { ImpactResult, RecommendedPlanResponse, RecommendedPlanSet } from './types';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

const makeRequest = async <T>(url: string, options?: RequestInit): Promise<T> => {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      ...options,
    });
  } catch {
    throw new ApiError('Network error - please check your connection and try again.', 0);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error ?? 'Something went wrong. Please try again.', res.status);
  }
  return (await res.json()) as T;
};

/**
 * Calls the Crescendo API's public impact endpoint.
 */
export async function fetchImpact(apiBaseUrl: string, channel: string): Promise<ImpactResult> {
  const url = `${apiBaseUrl.replace(/\/$/, '')}/api/impact`;

  return makeRequest<ImpactResult>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ channel }),
  });
}

interface RecommendedPlanOptions {
  avgViews: number;
  uploadsPerMonth: number;
}

/**
 * Calls api-backend to get the most appropriate plan for the user's channel,
 * based on monthly upload cadence and AVV
 */
export const fetchRecommendedPlan = async (
  planApiBaseUrl: string,
  options: RecommendedPlanOptions
): Promise<RecommendedPlanSet | null> => {
  const { avgViews = 0, uploadsPerMonth = 0 } = options;

  const url = `${planApiBaseUrl}/recommended-plan?avv=${avgViews}&monthly_uploads=${uploadsPerMonth}`;

  const res = await makeRequest<RecommendedPlanResponse>(url);

  return res.data;
};
